import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Card = ({ icon, title, body, testId, children }) => (
  <div className="min-h-screen bg-[#EEF1ED] grid place-items-center p-4">
    <div className="w-full max-w-md bg-white rounded-3xl p-8 sm:p-10 shadow-xl border border-slate-200/80 text-center pop-in" data-testid={testId}>
      {icon}
      <h1 className="mt-5 font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
      <p className="mt-2 text-[#4B5A54]">{body}</p>
      {children}
    </div>
  </div>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const [locId, setLocId] = useState(null);
  const [trial, setTrial] = useState(false);

  useEffect(() => {
    const sid = params.get("session_id");
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refresh();
          if (!stopped) { setLocId(data.location_id); setTrial(data.trial); setState("paid"); }
          return;
        }
      } catch { /* keep polling */ }
      if (++tries >= 15) return !stopped && setState("timeout");
      if (!stopped) setTimeout(poll, 2000);
    };
    if (sid) poll(); else setState("timeout");
    return () => { stopped = true; };
  }, [params, refresh]);

  if (state === "checking")
    return <Card testId="payment-checking" icon={<Loader2 className="w-12 h-12 mx-auto animate-spin text-[#2F5D4A]" />} title="Confirming payment…" body="Hang tight, this only takes a few seconds." />;
  if (state === "paid")
    return (
      <Card testId="payment-success-card" icon={<CheckCircle2 className="w-14 h-14 mx-auto text-[#2E6F8E]" />} title={trial ? "Your free month has started!" : "You're all set!"} body={trial ? "Enjoy 30 days free. Your survey link and QR code are ready." : "Your subscription is active. Your survey link and QR code are ready."}>
        <Link to={locId ? `/dashboard?loc=${locId}` : "/dashboard"} className="btn-primary mt-8 w-full justify-center !py-3" data-testid="payment-go-dashboard">Go to my dashboard</Link>
      </Card>
    );
  return (
    <Card testId="payment-timeout-card" icon={<XCircle className="w-14 h-14 mx-auto text-amber-500" />} title="Still processing" body="We couldn't confirm your payment yet. Check your dashboard in a minute.">
      <Link to="/dashboard" className="btn-secondary mt-8 w-full justify-center" data-testid="payment-timeout-dashboard">Back to dashboard</Link>
    </Card>
  );
}

export function PaymentCancel() {
  return (
    <Card testId="payment-cancel-card" icon={<XCircle className="w-14 h-14 mx-auto text-[#778580]" />} title="Checkout canceled" body="No charge was made. You can activate your subscription any time.">
      <Link to="/dashboard" className="btn-primary mt-8 w-full justify-center" data-testid="payment-cancel-dashboard">Back to dashboard</Link>
    </Card>
  );
}
